/**
 * Financial Excel Export Utility
 * Generates Excel files with formulas for financial sections
 */
import ExcelJS from 'exceljs';
import axios from 'axios';

// Financial section IDs
const FINANCIAL_SECTIONS = [
  'investment_plans',
  'pricing',
  'revenue_forecast',
  'cashflow',
  'pnl'
];

// Helper to parse financial data from AI-generated content
function extractFinancialData(content, sectionId) {
  // Default structure based on section type
  const defaults = {
    investment_plans: {
      totalInvestment: 50000,
      breakdown: [
        { category: 'Technology & Development', amount: 20000, percentage: 40 },
        { category: 'Marketing & Sales', amount: 10000, percentage: 20 },
        { category: 'Operations', amount: 8000, percentage: 16 },
        { category: 'Legal & Compliance', amount: 5000, percentage: 10 },
        { category: 'Working Capital', amount: 5000, percentage: 10 },
        { category: 'Contingency', amount: 2000, percentage: 4 }
      ]
    },
    revenue_forecast: {
      years: [1, 2, 3, 4, 5],
      revenue: [50000, 150000, 400000, 800000, 1500000],
      customers: [50, 200, 600, 1200, 2500],
      avgRevPerCustomer: [1000, 750, 667, 667, 600]
    },
    cashflow: {
      months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      openingBalance: 50000,
      receipts: {
        subscriptions: [2000, 3000, 4500, 6000, 8000, 10000, 12000, 15000, 18000, 22000, 26000, 30000],
        otherRevenue: [0, 0, 500, 500, 1000, 1000, 1500, 1500, 2000, 2000, 2500, 3000]
      },
      payments: {
        salaries: [5000, 5000, 5000, 6000, 6000, 6000, 7000, 7000, 7000, 8000, 8000, 8000],
        rent: [1000, 1000, 1000, 1000, 1000, 1000, 1200, 1200, 1200, 1200, 1200, 1200],
        marketing: [2000, 2500, 3000, 3000, 3500, 4000, 4000, 4500, 5000, 5000, 5500, 6000],
        software: [500, 500, 500, 600, 600, 600, 700, 700, 700, 800, 800, 800],
        utilities: [200, 200, 200, 200, 200, 200, 250, 250, 250, 250, 250, 250],
        insurance: [100, 100, 100, 100, 100, 100, 100, 100, 100, 100, 100, 100],
        professional: [500, 500, 500, 500, 500, 500, 600, 600, 600, 600, 600, 600],
        other: [200, 200, 200, 200, 200, 200, 200, 200, 200, 200, 200, 200]
      }
    },
    pnl: {
      years: [1, 2, 3, 4, 5],
      revenue: [50000, 150000, 400000, 800000, 1500000],
      costOfSales: [10000, 30000, 80000, 160000, 300000],
      salaries: [72000, 120000, 200000, 350000, 500000],
      rent: [12000, 14400, 17280, 20736, 24883],
      marketing: [15000, 40000, 80000, 150000, 250000],
      software: [6000, 8000, 12000, 18000, 25000],
      utilities: [2400, 2880, 3456, 4147, 4977],
      insurance: [1200, 1440, 1728, 2074, 2488],
      professional: [6000, 7200, 8640, 10368, 12442],
      other: [2400, 3000, 4000, 5000, 6000]
    }
  };

  return defaults[sectionId] || {};
}

// Style definitions
const headerStyle = {
  font: { bold: true, color: { argb: 'FFFFFFFF' }, size: 12 },
  fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDC2626' } },
  alignment: { horizontal: 'center', vertical: 'middle' },
  border: {
    top: { style: 'thin', color: { argb: 'FF000000' } },
    left: { style: 'thin', color: { argb: 'FF000000' } },
    bottom: { style: 'thin', color: { argb: 'FF000000' } },
    right: { style: 'thin', color: { argb: 'FF000000' } }
  }
};

const subHeaderStyle = {
  font: { bold: true, color: { argb: 'FF1E293B' }, size: 11 },
  fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } },
  alignment: { horizontal: 'left', vertical: 'middle' },
  border: {
    top: { style: 'thin', color: { argb: 'FFCBD5E1' } },
    left: { style: 'thin', color: { argb: 'FFCBD5E1' } },
    bottom: { style: 'thin', color: { argb: 'FFCBD5E1' } },
    right: { style: 'thin', color: { argb: 'FFCBD5E1' } }
  }
};

const currencyStyle = {
  numFmt: '£#,##0.00',
  alignment: { horizontal: 'right', vertical: 'middle' },
  border: {
    top: { style: 'thin', color: { argb: 'FFCBD5E1' } },
    left: { style: 'thin', color: { argb: 'FFCBD5E1' } },
    bottom: { style: 'thin', color: { argb: 'FFCBD5E1' } },
    right: { style: 'thin', color: { argb: 'FFCBD5E1' } }
  }
};

const percentStyle = {
  numFmt: '0.0%',
  alignment: { horizontal: 'right', vertical: 'middle' },
  border: {
    top: { style: 'thin', color: { argb: 'FFCBD5E1' } },
    left: { style: 'thin', color: { argb: 'FFCBD5E1' } },
    bottom: { style: 'thin', color: { argb: 'FFCBD5E1' } },
    right: { style: 'thin', color: { argb: 'FFCBD5E1' } }
  }
};

const totalStyle = {
  font: { bold: true },
  fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF2F2' } },
  numFmt: '£#,##0.00',
  alignment: { horizontal: 'right', vertical: 'middle' },
  border: {
    top: { style: 'medium', color: { argb: 'FFDC2626' } },
    left: { style: 'thin', color: { argb: 'FFCBD5E1' } },
    bottom: { style: 'medium', color: { argb: 'FFDC2626' } },
    right: { style: 'thin', color: { argb: 'FFCBD5E1' } }
  }
};

// Apply style to cell
function applyStyle(cell, style) {
  if (style.font) cell.font = style.font;
  if (style.fill) cell.fill = style.fill;
  if (style.alignment) cell.alignment = style.alignment;
  if (style.border) cell.border = style.border;
  if (style.numFmt) cell.numFmt = style.numFmt;
}

// Create Investment Plans sheet
function createInvestmentPlansSheet(workbook, data) {
  const ws = workbook.addWorksheet('Investment Plans');
  
  // Title
  ws.mergeCells('A1:D1');
  const titleCell = ws.getCell('A1');
  titleCell.value = 'Use of Investment Breakdown';
  titleCell.font = { bold: true, size: 16, color: { argb: 'FFDC2626' } };
  titleCell.alignment = { horizontal: 'center' };
  
  // Total Investment
  ws.getCell('A3').value = 'Total Investment Required:';
  ws.getCell('A3').font = { bold: true };
  ws.getCell('B3').value = data.totalInvestment;
  applyStyle(ws.getCell('B3'), currencyStyle);
  
  // Headers
  ws.getRow(5).values = ['Category', 'Amount (£)', 'Percentage', 'Notes'];
  ws.getRow(5).eachCell((cell) => applyStyle(cell, headerStyle));
  
  // Data rows with formulas
  let rowNum = 6;
  data.breakdown.forEach((item, idx) => {
    ws.getCell(`A${rowNum}`).value = item.category;
    ws.getCell(`B${rowNum}`).value = item.amount;
    applyStyle(ws.getCell(`B${rowNum}`), currencyStyle);
    // Formula for percentage
    ws.getCell(`C${rowNum}`).value = { formula: `B${rowNum}/$B$3` };
    applyStyle(ws.getCell(`C${rowNum}`), percentStyle);
    ws.getCell(`D${rowNum}`).value = '';
    rowNum++;
  });
  
  // Total row with SUM formula
  ws.getCell(`A${rowNum}`).value = 'TOTAL';
  ws.getCell(`A${rowNum}`).font = { bold: true };
  ws.getCell(`B${rowNum}`).value = { formula: `SUM(B6:B${rowNum - 1})` };
  applyStyle(ws.getCell(`B${rowNum}`), totalStyle);
  ws.getCell(`C${rowNum}`).value = { formula: `SUM(C6:C${rowNum - 1})` };
  applyStyle(ws.getCell(`C${rowNum}`), { ...percentStyle, ...totalStyle });
  
  // Column widths
  ws.getColumn('A').width = 30;
  ws.getColumn('B').width = 15;
  ws.getColumn('C').width = 12;
  ws.getColumn('D').width = 30;
  
  return ws;
}

// Create Revenue Forecast sheet
function createRevenueForecastSheet(workbook, data) {
  const ws = workbook.addWorksheet('Revenue Forecast');
  
  // Title
  ws.mergeCells('A1:F1');
  const titleCell = ws.getCell('A1');
  titleCell.value = '5-Year Revenue Forecast';
  titleCell.font = { bold: true, size: 16, color: { argb: 'FFDC2626' } };
  titleCell.alignment = { horizontal: 'center' };
  
  // Headers
  ws.getRow(3).values = ['Metric', 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5'];
  ws.getRow(3).eachCell((cell) => applyStyle(cell, headerStyle));
  
  // Revenue row
  ws.getCell('A4').value = 'Revenue (£)';
  applyStyle(ws.getCell('A4'), subHeaderStyle);
  for (let i = 0; i < 5; i++) {
    const col = String.fromCharCode(66 + i); // B, C, D, E, F
    ws.getCell(`${col}4`).value = data.revenue[i];
    applyStyle(ws.getCell(`${col}4`), currencyStyle);
  }
  
  // Customers row
  ws.getCell('A5').value = 'Total Customers';
  applyStyle(ws.getCell('A5'), subHeaderStyle);
  for (let i = 0; i < 5; i++) {
    const col = String.fromCharCode(66 + i);
    ws.getCell(`${col}5`).value = data.customers[i];
    ws.getCell(`${col}5`).alignment = { horizontal: 'right' };
  }
  
  // Avg Revenue per Customer (formula)
  ws.getCell('A6').value = 'Avg Revenue/Customer (£)';
  applyStyle(ws.getCell('A6'), subHeaderStyle);
  for (let i = 0; i < 5; i++) {
    const col = String.fromCharCode(66 + i);
    ws.getCell(`${col}6`).value = { formula: `${col}4/${col}5` };
    applyStyle(ws.getCell(`${col}6`), currencyStyle);
  }
  
  // Growth rate row (formula)
  ws.getCell('A7').value = 'YoY Growth Rate';
  applyStyle(ws.getCell('A7'), subHeaderStyle);
  ws.getCell('B7').value = 'N/A';
  ws.getCell('B7').alignment = { horizontal: 'center' };
  for (let i = 1; i < 5; i++) {
    const col = String.fromCharCode(66 + i);
    const prevCol = String.fromCharCode(65 + i);
    ws.getCell(`${col}7`).value = { formula: `(${col}4-${prevCol}4)/${prevCol}4` };
    applyStyle(ws.getCell(`${col}7`), percentStyle);
  }
  
  // Column widths
  ws.getColumn('A').width = 25;
  for (let i = 1; i <= 5; i++) {
    ws.getColumn(String.fromCharCode(65 + i)).width = 14;
  }
  
  return ws;
}

// Create Cash Flow Forecast sheet
function createCashFlowSheet(workbook, data) {
  const ws = workbook.addWorksheet('Cash Flow Forecast');
  
  // Title
  ws.mergeCells('A1:N1');
  const titleCell = ws.getCell('A1');
  titleCell.value = '12-Month Cash Flow Forecast';
  titleCell.font = { bold: true, size: 16, color: { argb: 'FFDC2626' } };
  titleCell.alignment = { horizontal: 'center' };
  
  // Headers
  const headerRow = ['Item', ...data.months, 'Total'];
  ws.getRow(3).values = headerRow;
  ws.getRow(3).eachCell((cell) => applyStyle(cell, headerStyle));
  
  let rowNum = 4;
  
  // Opening Balance
  ws.getCell(`A${rowNum}`).value = 'Opening Balance';
  applyStyle(ws.getCell(`A${rowNum}`), subHeaderStyle);
  ws.getCell('B4').value = data.openingBalance;
  applyStyle(ws.getCell('B4'), currencyStyle);
  // Opening balance for subsequent months = previous closing balance
  for (let i = 1; i < 12; i++) {
    const col = String.fromCharCode(67 + i); // C onwards
    const lastClosingRow = rowNum + 17; // Row where closing balance will be
    const prevCol = String.fromCharCode(66 + i); // Previous column
    ws.getCell(`${col}${rowNum}`).value = { formula: `${prevCol}${lastClosingRow}` };
    applyStyle(ws.getCell(`${col}${rowNum}`), currencyStyle);
  }
  rowNum++;
  
  // RECEIPTS section
  ws.getCell(`A${rowNum}`).value = 'RECEIPTS';
  ws.getCell(`A${rowNum}`).font = { bold: true, color: { argb: 'FF16A34A' } };
  rowNum++;
  
  // Subscriptions
  ws.getCell(`A${rowNum}`).value = 'Subscription Revenue';
  for (let i = 0; i < 12; i++) {
    const col = String.fromCharCode(66 + i);
    ws.getCell(`${col}${rowNum}`).value = data.receipts.subscriptions[i];
    applyStyle(ws.getCell(`${col}${rowNum}`), currencyStyle);
  }
  ws.getCell(`N${rowNum}`).value = { formula: `SUM(B${rowNum}:M${rowNum})` };
  applyStyle(ws.getCell(`N${rowNum}`), totalStyle);
  const subsRow = rowNum;
  rowNum++;
  
  // Other Revenue
  ws.getCell(`A${rowNum}`).value = 'Other Revenue';
  for (let i = 0; i < 12; i++) {
    const col = String.fromCharCode(66 + i);
    ws.getCell(`${col}${rowNum}`).value = data.receipts.otherRevenue[i];
    applyStyle(ws.getCell(`${col}${rowNum}`), currencyStyle);
  }
  ws.getCell(`N${rowNum}`).value = { formula: `SUM(B${rowNum}:M${rowNum})` };
  applyStyle(ws.getCell(`N${rowNum}`), totalStyle);
  const otherRevRow = rowNum;
  rowNum++;
  
  // Total Receipts
  ws.getCell(`A${rowNum}`).value = 'Total Receipts';
  ws.getCell(`A${rowNum}`).font = { bold: true };
  for (let i = 0; i < 12; i++) {
    const col = String.fromCharCode(66 + i);
    ws.getCell(`${col}${rowNum}`).value = { formula: `${col}${subsRow}+${col}${otherRevRow}` };
    applyStyle(ws.getCell(`${col}${rowNum}`), totalStyle);
  }
  ws.getCell(`N${rowNum}`).value = { formula: `SUM(B${rowNum}:M${rowNum})` };
  applyStyle(ws.getCell(`N${rowNum}`), totalStyle);
  const totalReceiptsRow = rowNum;
  rowNum++;
  
  // Empty row
  rowNum++;
  
  // PAYMENTS section
  ws.getCell(`A${rowNum}`).value = 'PAYMENTS';
  ws.getCell(`A${rowNum}`).font = { bold: true, color: { argb: 'FFDC2626' } };
  rowNum++;
  
  // Payment categories
  const paymentCategories = [
    { name: 'Salaries', data: data.payments.salaries },
    { name: 'Rent', data: data.payments.rent },
    { name: 'Marketing', data: data.payments.marketing },
    { name: 'Software', data: data.payments.software },
    { name: 'Utilities', data: data.payments.utilities },
    { name: 'Insurance', data: data.payments.insurance },
    { name: 'Professional Fees', data: data.payments.professional },
    { name: 'Other', data: data.payments.other }
  ];
  
  const paymentStartRow = rowNum;
  paymentCategories.forEach((cat) => {
    ws.getCell(`A${rowNum}`).value = cat.name;
    for (let i = 0; i < 12; i++) {
      const col = String.fromCharCode(66 + i);
      ws.getCell(`${col}${rowNum}`).value = cat.data[i];
      applyStyle(ws.getCell(`${col}${rowNum}`), currencyStyle);
    }
    ws.getCell(`N${rowNum}`).value = { formula: `SUM(B${rowNum}:M${rowNum})` };
    applyStyle(ws.getCell(`N${rowNum}`), currencyStyle);
    rowNum++;
  });
  const paymentEndRow = rowNum - 1;
  
  // Total Payments
  ws.getCell(`A${rowNum}`).value = 'Total Payments';
  ws.getCell(`A${rowNum}`).font = { bold: true };
  for (let i = 0; i < 12; i++) {
    const col = String.fromCharCode(66 + i);
    ws.getCell(`${col}${rowNum}`).value = { formula: `SUM(${col}${paymentStartRow}:${col}${paymentEndRow})` };
    applyStyle(ws.getCell(`${col}${rowNum}`), totalStyle);
  }
  ws.getCell(`N${rowNum}`).value = { formula: `SUM(B${rowNum}:M${rowNum})` };
  applyStyle(ws.getCell(`N${rowNum}`), totalStyle);
  const totalPaymentsRow = rowNum;
  rowNum++;
  
  // Empty row
  rowNum++;
  
  // Net Cash Flow
  ws.getCell(`A${rowNum}`).value = 'Net Cash Flow';
  ws.getCell(`A${rowNum}`).font = { bold: true };
  for (let i = 0; i < 12; i++) {
    const col = String.fromCharCode(66 + i);
    ws.getCell(`${col}${rowNum}`).value = { formula: `${col}${totalReceiptsRow}-${col}${totalPaymentsRow}` };
    applyStyle(ws.getCell(`${col}${rowNum}`), totalStyle);
  }
  ws.getCell(`N${rowNum}`).value = { formula: `SUM(B${rowNum}:M${rowNum})` };
  applyStyle(ws.getCell(`N${rowNum}`), totalStyle);
  const netCashFlowRow = rowNum;
  rowNum++;
  
  // Closing Balance
  ws.getCell(`A${rowNum}`).value = 'Closing Balance';
  ws.getCell(`A${rowNum}`).font = { bold: true, color: { argb: 'FF1E40AF' } };
  for (let i = 0; i < 12; i++) {
    const col = String.fromCharCode(66 + i);
    ws.getCell(`${col}${rowNum}`).value = { formula: `${col}4+${col}${netCashFlowRow}` };
    applyStyle(ws.getCell(`${col}${rowNum}`), totalStyle);
    ws.getCell(`${col}${rowNum}`).font = { bold: true, color: { argb: 'FF1E40AF' } };
  }
  
  // Column widths
  ws.getColumn('A').width = 20;
  for (let i = 1; i <= 13; i++) {
    ws.getColumn(String.fromCharCode(65 + i)).width = 12;
  }
  
  return ws;
}

// Create P&L Forecast sheet
function createPnLSheet(workbook, data) {
  const ws = workbook.addWorksheet('P&L Forecast');
  
  // Title
  ws.mergeCells('A1:F1');
  const titleCell = ws.getCell('A1');
  titleCell.value = '5-Year Profit & Loss Forecast';
  titleCell.font = { bold: true, size: 16, color: { argb: 'FFDC2626' } };
  titleCell.alignment = { horizontal: 'center' };
  
  // Headers
  ws.getRow(3).values = ['Item', 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5'];
  ws.getRow(3).eachCell((cell) => applyStyle(cell, headerStyle));
  
  let rowNum = 4;
  
  // Revenue
  ws.getCell(`A${rowNum}`).value = 'Revenue';
  applyStyle(ws.getCell(`A${rowNum}`), { ...subHeaderStyle, font: { bold: true, color: { argb: 'FF16A34A' } } });
  for (let i = 0; i < 5; i++) {
    const col = String.fromCharCode(66 + i);
    ws.getCell(`${col}${rowNum}`).value = data.revenue[i];
    applyStyle(ws.getCell(`${col}${rowNum}`), currencyStyle);
  }
  const revenueRow = rowNum;
  rowNum++;
  
  // Cost of Sales
  ws.getCell(`A${rowNum}`).value = 'Cost of Sales';
  for (let i = 0; i < 5; i++) {
    const col = String.fromCharCode(66 + i);
    ws.getCell(`${col}${rowNum}`).value = data.costOfSales[i];
    applyStyle(ws.getCell(`${col}${rowNum}`), currencyStyle);
  }
  const cosRow = rowNum;
  rowNum++;
  
  // Gross Profit
  ws.getCell(`A${rowNum}`).value = 'Gross Profit';
  ws.getCell(`A${rowNum}`).font = { bold: true };
  for (let i = 0; i < 5; i++) {
    const col = String.fromCharCode(66 + i);
    ws.getCell(`${col}${rowNum}`).value = { formula: `${col}${revenueRow}-${col}${cosRow}` };
    applyStyle(ws.getCell(`${col}${rowNum}`), totalStyle);
  }
  const grossProfitRow = rowNum;
  rowNum++;
  
  // Empty row
  rowNum++;
  
  // Operating Expenses header
  ws.getCell(`A${rowNum}`).value = 'Operating Expenses';
  ws.getCell(`A${rowNum}`).font = { bold: true, color: { argb: 'FFDC2626' } };
  rowNum++;
  
  // Expense categories
  const expenseCategories = [
    { name: 'Salaries', data: data.salaries },
    { name: 'Rent', data: data.rent },
    { name: 'Marketing', data: data.marketing },
    { name: 'Software', data: data.software },
    { name: 'Utilities', data: data.utilities },
    { name: 'Insurance', data: data.insurance },
    { name: 'Professional Fees', data: data.professional },
    { name: 'Other', data: data.other }
  ];
  
  const expenseStartRow = rowNum;
  expenseCategories.forEach((cat) => {
    ws.getCell(`A${rowNum}`).value = cat.name;
    for (let i = 0; i < 5; i++) {
      const col = String.fromCharCode(66 + i);
      ws.getCell(`${col}${rowNum}`).value = cat.data[i];
      applyStyle(ws.getCell(`${col}${rowNum}`), currencyStyle);
    }
    rowNum++;
  });
  const expenseEndRow = rowNum - 1;
  
  // Total Operating Expenses
  ws.getCell(`A${rowNum}`).value = 'Total Operating Expenses';
  ws.getCell(`A${rowNum}`).font = { bold: true };
  for (let i = 0; i < 5; i++) {
    const col = String.fromCharCode(66 + i);
    ws.getCell(`${col}${rowNum}`).value = { formula: `SUM(${col}${expenseStartRow}:${col}${expenseEndRow})` };
    applyStyle(ws.getCell(`${col}${rowNum}`), totalStyle);
  }
  const totalExpenseRow = rowNum;
  rowNum++;
  
  // Empty row
  rowNum++;
  
  // EBITDA
  ws.getCell(`A${rowNum}`).value = 'EBITDA';
  ws.getCell(`A${rowNum}`).font = { bold: true };
  for (let i = 0; i < 5; i++) {
    const col = String.fromCharCode(66 + i);
    ws.getCell(`${col}${rowNum}`).value = { formula: `${col}${grossProfitRow}-${col}${totalExpenseRow}` };
    applyStyle(ws.getCell(`${col}${rowNum}`), totalStyle);
  }
  const ebitdaRow = rowNum;
  rowNum++;
  
  // Corporation Tax (25%)
  ws.getCell(`A${rowNum}`).value = 'Corporation Tax (25%)';
  for (let i = 0; i < 5; i++) {
    const col = String.fromCharCode(66 + i);
    ws.getCell(`${col}${rowNum}`).value = { formula: `IF(${col}${ebitdaRow}>0,${col}${ebitdaRow}*0.25,0)` };
    applyStyle(ws.getCell(`${col}${rowNum}`), currencyStyle);
  }
  const taxRow = rowNum;
  rowNum++;
  
  // Net Profit
  ws.getCell(`A${rowNum}`).value = 'Net Profit';
  ws.getCell(`A${rowNum}`).font = { bold: true, size: 12, color: { argb: 'FF16A34A' } };
  for (let i = 0; i < 5; i++) {
    const col = String.fromCharCode(66 + i);
    ws.getCell(`${col}${rowNum}`).value = { formula: `${col}${ebitdaRow}-${col}${taxRow}` };
    applyStyle(ws.getCell(`${col}${rowNum}`), totalStyle);
    ws.getCell(`${col}${rowNum}`).font = { bold: true, color: { argb: 'FF16A34A' } };
  }
  const netProfitRow = rowNum;
  rowNum++;
  
  // Empty row
  rowNum++;
  
  // Net Profit Margin
  ws.getCell(`A${rowNum}`).value = 'Net Profit Margin';
  ws.getCell(`A${rowNum}`).font = { italic: true };
  for (let i = 0; i < 5; i++) {
    const col = String.fromCharCode(66 + i);
    ws.getCell(`${col}${rowNum}`).value = { formula: `${col}${netProfitRow}/${col}${revenueRow}` };
    applyStyle(ws.getCell(`${col}${rowNum}`), percentStyle);
  }
  
  // Column widths
  ws.getColumn('A').width = 25;
  for (let i = 1; i <= 5; i++) {
    ws.getColumn(String.fromCharCode(65 + i)).width = 14;
  }
  
  return ws;
}

// Main export function
export async function exportFinancialExcel(proposal, sections, token, API) {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'InnovatorFounderVisa';
  workbook.created = new Date();
  
  // Set workbook properties
  workbook.properties = {
    title: `${proposal.idea_title} - Financial Plan`,
    subject: 'UK Innovator Founder Visa Financial Plan',
    company: 'InnovatorFounderVisa'
  };
  
  // Fetch all section content
  const sectionData = {};
  for (const sectionId of FINANCIAL_SECTIONS) {
    try {
      const res = await axios.get(
        `${API}/ai/proposals/${proposal.id}/sections/${sectionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      sectionData[sectionId] = res.data;
    } catch (e) {
      // Section doesn't exist yet
      sectionData[sectionId] = null;
    }
  }
  
  // Create Cover sheet
  const coverSheet = workbook.addWorksheet('Cover');
  coverSheet.mergeCells('A2:D2');
  coverSheet.getCell('A2').value = proposal.idea_title;
  coverSheet.getCell('A2').font = { bold: true, size: 24, color: { argb: 'FFDC2626' } };
  coverSheet.getCell('A2').alignment = { horizontal: 'center' };
  
  coverSheet.mergeCells('A4:D4');
  coverSheet.getCell('A4').value = 'Financial Plan';
  coverSheet.getCell('A4').font = { bold: true, size: 18, color: { argb: 'FF1E293B' } };
  coverSheet.getCell('A4').alignment = { horizontal: 'center' };
  
  coverSheet.getCell('A7').value = 'Generated:';
  coverSheet.getCell('B7').value = new Date().toLocaleDateString('en-GB');
  
  coverSheet.getCell('A8').value = 'Category:';
  coverSheet.getCell('B8').value = proposal.category || 'Technology';
  
  coverSheet.getColumn('A').width = 15;
  coverSheet.getColumn('B').width = 30;
  
  // Create financial sheets
  const investmentData = extractFinancialData(sectionData.investment_plans?.content, 'investment_plans');
  const revenueData = extractFinancialData(sectionData.revenue_forecast?.content, 'revenue_forecast');
  const cashflowData = extractFinancialData(sectionData.cashflow?.content, 'cashflow');
  const pnlData = extractFinancialData(sectionData.pnl?.content, 'pnl');
  
  createInvestmentPlansSheet(workbook, investmentData);
  createRevenueForecastSheet(workbook, revenueData);
  createCashFlowSheet(workbook, cashflowData);
  createPnLSheet(workbook, pnlData);
  
  // Generate blob and download
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  
  // Create download link
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const filename = `${proposal.idea_title.replace(/[^a-zA-Z0-9]/g, '_')}_Financial_Plan.xlsx`;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
  
  return filename;
}
